/**
 * Table inputs on the wire (04_frontend_spec.md §5.9): which rows a command is
 * sent, and where a wire row's error belongs on screen.
 *
 * The UI holds every row, ticked or not; unticked rows are left out of the
 * body. A row's position on the wire is therefore its place among the ticked
 * rows, and a field error arrives on that wire position (`lines.1.amount`).
 *
 * A table may hold ONE level of nested tables in its rows (`lines[].destinations[]`,
 * see `NestedTableDescriptor`). Ticks are kept per table path on the UI's row
 * numbers: `lines` for the parent rows, `lines.2.destinations` for the nested
 * rows of the third parent row. Nothing here goes deeper than that one level.
 */
import type { TableDescriptor } from "./widgets";

/** Ticked rows (UI row numbers, ascending) per selectable table path. */
export type SelectedRows = Readonly<Record<string, readonly number[]>>;

export interface TableAt {
  path: string;
  table: Pick<TableDescriptor, "selectable" | "nested">;
}

/** The body path of one parent row's nested table. */
export function nestedTablePath(
  tablePath: string,
  row: number,
  field: string,
): string {
  return `${tablePath}.${row}.${field}`;
}

/** Rows that are sent, each with the UI row it is: all of them, or the ticked ones. */
function sentRows(
  path: string,
  isSelectable: boolean,
  rows: readonly unknown[],
  selected: SelectedRows,
): { ui: number; row: unknown }[] {
  const ui = isSelectable
    ? (selected[path] ?? []).filter((n) => n >= 0 && n < rows.length)
    : rows.map((_, n) => n);
  return ui.map((n) => ({ ui: n, row: rows[n] }));
}

/**
 * A table's rows as sent: unticked rows out at each level. A nested table
 * inside an unticked parent goes with its parent.
 */
export function wireTableRows(
  { path, table }: TableAt,
  rows: unknown,
  selected: SelectedRows,
): unknown {
  if (!Array.isArray(rows)) return rows;
  return sentRows(path, table.selectable, rows, selected).map(({ ui, row }) => {
    if (table.nested.length === 0 || row === null || typeof row !== "object")
      return row;
    const sent: Record<string, unknown> = {
      ...(row as Record<string, unknown>),
    };
    for (const nested of table.nested) {
      const nestedRows = sent[nested.field];
      if (!Array.isArray(nestedRows)) continue;
      sent[nested.field] = sentRows(
        nestedTablePath(path, ui, nested.field),
        nested.selectable,
        nestedRows,
        selected,
      ).map((r) => r.row);
    }
    return sent;
  });
}

/** The UI row a wire index is, or undefined when it is none. */
function uiRowOf(
  path: string,
  isSelectable: boolean,
  wireIndex: string | undefined,
  selected: SelectedRows,
): number | undefined {
  const wire = Number(wireIndex);
  if (wireIndex === undefined || !Number.isInteger(wire) || wire < 0)
    return undefined;
  return isSelectable ? (selected[path] ?? [])[wire] : wire;
}

/**
 * A wire error path (`lines.1.destinations.0.quantity`) as the UI rows it came
 * from (`lines.3.destinations.0.quantity`). A path that is not a row of a known
 * table, or names a row that has no UI position, comes back as it was.
 */
export function uiPathOf(
  wirePath: string,
  tables: readonly TableAt[],
  selected: SelectedRows,
): string {
  for (const { path, table } of tables) {
    const prefix = `${path}.`;
    if (!wirePath.startsWith(prefix)) continue;
    const [index, ...rest] = wirePath.slice(prefix.length).split(".");
    const parent = uiRowOf(path, table.selectable, index, selected);
    if (parent === undefined) return wirePath;
    const nested = table.nested.find((n) => n.field === rest[0]);
    if (!nested || rest.length < 2)
      return [path, String(parent), ...rest].join(".");
    const nestedPath = nestedTablePath(path, parent, nested.field);
    const child = uiRowOf(nestedPath, nested.selectable, rest[1], selected);
    if (child === undefined) return wirePath;
    return [nestedPath, String(child), ...rest.slice(2)].join(".");
  }
  return wirePath;
}
